/**
 * Validation Utilities
 * Functions for validating emails, phones, and other inputs
 */

// ============================================
// Regular Expressions
// ============================================
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^[\d\s+\-()]{6,20}$/;

/**
 * Validate email address
 * @param {string} email - Email to validate
 * @returns {boolean} True if valid email
 */
export function isValidEmail(email) {
  if (!email || typeof email !== "string") return false;
  return EMAIL_REGEX.test(email.trim());
}

/**
 * Validate phone number
 * @param {string|number} phone - Phone number to validate
 * @returns {boolean} True if valid phone
 */
export function isValidPhone(phone) {
  if (!phone) return false;
  
  // Convert to string if it's a number
  const phoneStr = String(phone).trim();
  
  // Spanish phone number validation (more flexible)
  // Accepts: 9 digits, or with +34, or with spaces/dashes
  const spanishPhoneRegex = /^(\+34\s?)?[6-9]\d{8}$|^[6-9]\d{2}\s?\d{3}\s?\d{3}$|^[6-9]\d{8}$/;
  
  // Remove spaces and dashes for validation
  const cleanPhone = phoneStr.replace(/[\s\-()]/g, '');
  
  return spanishPhoneRegex.test(cleanPhone) || PHONE_REGEX.test(phoneStr);
}

/**
 * Validate PDF file
 * @param {File} file - File to validate
 * @param {number} maxSizeBytes - Maximum file size in bytes
 * @returns {Object} { valid: boolean, error: string|null }
 */
export function validatePdfFile(file, maxSizeBytes = 10 * 1024 * 1024) {
  if (!file) {
    return { valid: false, error: "No se seleccionó ningún archivo" };
  }

  if (file.type !== "application/pdf") {
    return { valid: false, error: "El archivo debe ser un PDF" };
  }

  if (file.size > maxSizeBytes) {
    const maxMB = Math.round(maxSizeBytes / (1024 * 1024));
    return { valid: false, error: `El archivo supera ${maxMB} MB` };
  }

  return { valid: true, error: null };
}

/**
 * Validate DNI/NIF (Spanish ID)
 * Basic validation - checks format only
 * @param {string} dni - DNI to validate
 * @returns {boolean} True if valid format
 */
export function isValidDNI(dni) {
  if (!dni || typeof dni !== "string") return false;
  
  // Remove spaces and convert to uppercase
  const cleaned = dni.trim().toUpperCase().replace(/\s/g, "");
  
  // Basic format: 8 digits + 1 letter
  const dniRegex = /^\d{8}[A-Z]$/;
  
  return dniRegex.test(cleaned);
}

/**
 * Validate that a string is not empty
 * @param {string} value - Value to validate
 * @returns {boolean} True if not empty
 */
export function isNotEmpty(value) {
  return value && typeof value === "string" && value.trim().length > 0;
}

/**
 * Validate URL format
 * @param {string} url - URL to validate
 * @returns {boolean} True if valid URL
 */
export function isValidUrl(url) {
  if (!url || typeof url !== "string") return false;
  
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

/**
 * Sanitize string for safe display (prevent XSS)
 * @param {string} str - String to sanitize
 * @returns {string} Sanitized string
 */
export function sanitizeString(str) {
  if (!str || typeof str !== "string") return "";
  
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#x27;")
    .replace(/\//g, "&#x2F;");
}
